import type React from "react";
import { useState } from "react";
import { TableRow, TableCell, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { LogislySpxOnCall } from "../../types/logislySpxOnCall";
import { format } from "date-fns";
import DataTable from "../common/Table";
import SyncableChip from "../common/SyncableChip";
import type { PaginationResponse } from "../../types/paginationResponse";
import type { PaginationParam } from "../../types/paginationResponse";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

interface LogislySpxOnCallTableProps {
  items: LogislySpxOnCall[];
  loading?: boolean;
  pagination?: PaginationResponse | null;
  onPaginationChange?: (pagination: PaginationParam) => void;
}

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), "dd MMM yyyy, HH:mm");
  } catch {
    return dateString;
  }
};

const formatDateRange = (firstSeenAt: string, lastSeenAt: string) => {
  try {
    const firstDate = new Date(firstSeenAt);
    const lastDate = new Date(lastSeenAt);

    const firstFormatted = format(firstDate, "dd MMM yyyy, HH:mm");
    const lastTime = format(lastDate, "HH:mm");

    // Check if same year and month
    const sameYear = firstDate.getFullYear() === lastDate.getFullYear();
    const sameMonth = firstDate.getMonth() === lastDate.getMonth();
    const sameDay = firstDate.getDate() === lastDate.getDate();

    if (sameYear && sameMonth && sameDay) {
      // Same day: "15 Jan 2024, 10:00 - 10:05"
      const firstTime = format(firstDate, "HH:mm");
      return `${format(firstDate, "dd MMM yyyy")}, ${firstTime} - ${lastTime}`;
    }
    if (sameYear && sameMonth) {
      // Same month/year, different day: "15 Jan 2024, 10:00 - 16, 10:05"
      return `${format(firstDate, "dd MMM yyyy")}, ${format(firstDate, "HH:mm")} - ${format(lastDate, "dd")}, ${lastTime}`;
    }
    if (sameYear) {
      // Same year, different month: "15 Jan 2024, 10:00 - 16 Feb, 10:05"
      return `${format(firstDate, "dd MMM yyyy")}, ${format(firstDate, "HH:mm")} - ${format(lastDate, "dd MMM")}, ${lastTime}`;
    }
    // Different year: "15 Jan 2024, 10:00 - 16 Feb 2025, 10:05"
    return `${firstFormatted} - ${format(lastDate, "dd MMM yyyy, HH:mm")}`;
  } catch {
    return `${firstSeenAt} - ${lastSeenAt}`;
  }
};

const formatPrice = (price: string) => {
  const numPrice = Number.parseFloat(price);
  if (Number.isNaN(numPrice)) return price;
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(numPrice);
};

const LogislySpxOnCallTable: React.FC<LogislySpxOnCallTableProps> = ({
  items,
  loading,
  pagination,
  onPaginationChange,
}) => {
  const { t } = useTranslation();
  const [hoveredLocation, setHoveredLocation] = useState<string | null>(null);
  const [filteredLocation, setFilteredLocation] = useState<string | null>(null);

  // Filter items based on selected location
  const filteredItems = filteredLocation
    ? items.filter(
        (item) => item.origin === filteredLocation || item.destinations.includes(filteredLocation),
      )
    : items;

  const handleChipClick = (locationName: string) => {
    // If clicking the same location, clear the filter
    // Otherwise, set it as the filter
    if (filteredLocation === locationName) {
      setFilteredLocation(null);
    } else {
      setFilteredLocation(locationName);
    }
  };

  const columns = [
    {
      id: "firstLastSeenAt",
      label: t("Pertama/Terakhir Dilihat" as TranslationKeys),
      sortable: true,
    },
    { id: "standby", label: t("Waktu Muat" as TranslationKeys), sortable: true },
    { id: "truckType", label: t("Truck Type" as TranslationKeys), sortable: true },
    { id: "origin", label: t("Muat" as TranslationKeys), sortable: true },
    { id: "destinations", label: t("Bongkar" as TranslationKeys), sortable: false },
    { id: "orderNumber", label: t("Nomor Order" as TranslationKeys), sortable: true },
    { id: "price", label: t("Harga" as TranslationKeys), sortable: true, align: "right" as const },
  ];

  return (
    <DataTable
      data={filteredItems}
      columns={columns}
      loading={loading}
      showSearch={false}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      showRowsPerPage={true}
      renderRow={(item) => {
        return (
          <TableRow
            key={item._id}
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <TableCell>{formatDateRange(item.firstSeenAt, item.lastSeenAt)}</TableCell>
            <TableCell>{formatDate(item.standby)}</TableCell>
            <TableCell>{item.truckType}</TableCell>
            <TableCell>
              <SyncableChip
                label={item.origin}
                hoveredValue={hoveredLocation}
                filteredValue={filteredLocation}
                onMouseEnter={() => setHoveredLocation(item.origin)}
                onMouseLeave={() => setHoveredLocation(null)}
                onClick={() => handleChipClick(item.origin)}
              />
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {item.destinations.map((destination, index) => (
                  <SyncableChip
                    key={`${item._id}-dest-${index}`}
                    label={destination}
                    hoveredValue={hoveredLocation}
                    filteredValue={filteredLocation}
                    onMouseEnter={() => setHoveredLocation(destination)}
                    onMouseLeave={() => setHoveredLocation(null)}
                    onClick={() => handleChipClick(destination)}
                  />
                ))}
              </Box>
            </TableCell>
            <TableCell>{item.orderNumber}</TableCell>
            <TableCell align="right">{formatPrice(item.price)}</TableCell>
          </TableRow>
        );
      }}
    />
  );
};

export default LogislySpxOnCallTable;
